import React from "react";
import { View } from "react-native";
import {
  SortableContainer,
  SortableElement,
  SortableHandle
} from "react-sortable-hoc";
import { getIn } from "final-form";
import FormToManyField from "./FormToManyField";

// We make the HOC a regular React component so we can manipulate it easily
const Wrapper = SortableContainer(({ children }) => children);

// We make the HOC a regular React component so we can manipulate it easily
const SortableItem = SortableElement(({ children }) => children);

// We make the HOC a regular React component so we can manipulate it easily
const DragHandle = SortableHandle(({ children }) => children);

/**
 * Recalculates the sequence property in all of the items in the to-many relationship.
 * 
 * We increment the sequence between items by 10.
 */
const recalculateOrder = ({ form, name }) => {
  // Obtains the data from the form as an array
  let data = getIn(form.getState().values, name),
    sequence = 10;

  data = data.map(value => {
    try {
      // Get the record in the relationship with an updated sequence
      return { ...value, sequence };
    } finally {
      // This is executed after the return
      sequence += 10;
    }
  });

  // This is what actually updates the form state so that the form can be saved
  form.change(name, data);
};

/**
 * A version of the FormToManyField that allows to drag and drop the items to change the order.
 * 
 * Once the new sequence is determined by the user, the sequence property is updated for each record.
 */
const FormSortableToManyField = ({ children, ...restProps }) => (
  <FormToManyField
    {...restProps}
    fieldWrapper={({ form, name, move, children: component }) => (
      <Wrapper
        useDragHandle={true}
        axis="xy"
        onSortEnd={({ oldIndex, newIndex }) => {
          // Moves item from one place in the array to another
          move(oldIndex, newIndex);

          // Recalculate the sequence based on the array change above
          recalculateOrder({ form, name });
        }}
      >
        {component}
      </Wrapper>
    )}
    // When a new item is added, we recalculate everything so the new item has the correct sequence
    onAdd={({ form, name }) => recalculateOrder({ form, name })}
  >
    {({ index, name, isNew, ...renderProps }) => {
      // The new item at the end isn't sortable, so we wrap it in a dummy View
      const ItemWrapper = isNew ? View : SortableItem;

      return (
        <ItemWrapper index={index} key={name}>
          {children({ index, name, isNew, ...renderProps })}
        </ItemWrapper>
      );
    }}
  </FormToManyField>
);

export default FormSortableToManyField;

export { DragHandle };
